import { HeuristicPlanner, type Planner, type PlannerContext, type PlannerStep } from "@quill/core";

export type LocalModel = "mini" | "base" | "webllm";
type Field = "name" | "email" | "date" | "party";

// Task examples are the embedded equivalent of the Ternlight React-docs corpus:
// add representative language here instead of retraining the embedding model.
const BOOKING_EXAMPLES = [
  "book a restaurant reservation",
  "reserve a table for dinner",
  "fill out this reservation form",
  "Vikrant Pogula on 25 July 2025",
  "dinner tomorrow for four people",
  "get me a table under my name",
  "we need a quiet table for two",
];
const OTHER_EXAMPLES = ["cancel a reservation", "change an existing booking", "ask a restaurant question"];
const WEBLLM_MODEL = "Qwen2.5-0.5B-Instruct-q4f16_1-MLC";
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const QUESTIONS: Record<Field, string> = {
  name: "What name should I put on the reservation?",
  email: "What email should receive the confirmation?",
  date: "What date would you like? You can say today, tomorrow, or YYYY-MM-DD.",
  party: "How many guests? This demo supports 1, 2, 4, or 6.",
};

export class TernlightPlanner implements Planner {
  private profile: Record<string, string> = {};
  private awaiting?: Field;
  private formPlanner?: HeuristicPlanner;

  constructor(
    private readonly model: () => LocalModel,
    private readonly setStatus: (status: string) => void = () => undefined,
  ) {}

  async next(ctx: PlannerContext): Promise<PlannerStep> {
    if (ctx.step === 0) {
      const model = this.model();
      this.profile = extract(ctx.task);
      if (this.profile.name) {
        this.profile.email ??= `${this.profile.name.toLowerCase().replace(/[^a-z0-9]/g, "")}@gmail.com`;
        this.profile.party ??= "2 guests";
      }
      this.awaiting = undefined;
      this.formPlanner = undefined;
      let isBooking = true;
      if (model === "webllm") {
        try {
          const result = await understandWithWebLLM(ctx.task, this.setStatus);
          this.profile = { ...result.profile, ...this.profile };
          isBooking = result.intent === "book";
        } catch (error) {
          this.setStatus("WebLLM unavailable — choose Ternlight Mini or Base");
          return { text: error instanceof Error ? error.message : String(error), toolCalls: [], done: true };
        }
      } else {
        const ternlight = await (model === "base" ? import("@ternlight/base") : import("@ternlight/mini"));
        const intent = ternlight.similar(ctx.task, [...BOOKING_EXAMPLES, ...OTHER_EXAMPLES], { topK: 1 })[0]?.text;
        isBooking = !!intent && BOOKING_EXAMPLES.includes(intent);
      }
      if (!isBooking) {
        return {
          text: "This local embedding demo can book a new reservation, but it can't handle that request yet.",
          toolCalls: [],
          done: true,
        };
      }
    }

    if (this.awaiting) {
      const answer = [...ctx.history].reverse().find((m) => m.role === "tool" && m.toolName === "ask_user")?.content;
      const value = answer && normalize(this.awaiting, String(JSON.parse(answer).answer ?? ""));
      if (value) {
        this.profile[this.awaiting] = value;
        this.awaiting = undefined;
      }
    }

    const missing = (["name", "email", "date", "party"] as Field[]).find((field) => !this.profile[field]);
    if (missing) {
      this.awaiting = missing;
      return {
        text: QUESTIONS[missing],
        toolCalls: [{ id: `local-${ctx.step}`, name: "ask_user", input: { question: QUESTIONS[missing] } }],
        done: false,
      };
    }

    this.formPlanner ??= new HeuristicPlanner({
      profile: this.profile,
      submit: { testid: "place-order" },
    });
    return this.formPlanner.next(ctx);
  }
}

let webllmEngine: Promise<import("@mlc-ai/web-llm").MLCEngineInterface> | undefined;

async function understandWithWebLLM(
  text: string,
  setStatus: (status: string) => void,
): Promise<{ intent: string; profile: Record<string, string> }> {
  if (!("gpu" in navigator)) throw new Error("WebLLM needs a browser with WebGPU enabled. Ternlight works without it.");
  const { CreateMLCEngine } = await import("@mlc-ai/web-llm");
  webllmEngine ??= CreateMLCEngine(WEBLLM_MODEL, {
    initProgressCallback: ({ progress }) => setStatus(`Loading local WebLLM model… ${Math.round(progress * 100)}%`),
  }).catch((error) => {
    webllmEngine = undefined;
    throw error;
  });
  const engine = await webllmEngine;
  setStatus("WebLLM is understanding your request locally…");
  const response = await engine.chat.completions.create({
    messages: [
      {
        role: "system",
        content: `Extract a new restaurant reservation request. Today is ${new Date().toISOString().slice(0, 10)}. Return only JSON with intent (book or other), name, email, date (YYYY-MM-DD), party (1, 2, 4, or 6), and notes. Use empty strings for missing fields.`,
      },
      { role: "user", content: text },
    ],
    response_format: { type: "json_object", schema: JSON.stringify({ type: "object" }) },
    temperature: 0,
    max_tokens: 120,
  });
  setStatus("WebLLM is validating the reservation details…");
  const value = JSON.parse(response.choices[0]?.message.content ?? "{}");
  const profile: Record<string, string> = {};
  for (const field of ["name", "email", "date", "party"] as Field[]) {
    const normalized = normalize(field, String(value[field] ?? ""));
    if (normalized) profile[field] = normalized;
  }
  if (typeof value.notes === "string" && value.notes.trim()) profile.notes = value.notes.trim();
  setStatus("Details ready — filling the form");
  return { intent: value.intent === "book" ? "book" : "other", profile };
}

function extract(text: string): Record<string, string> {
  const profile: Record<string, string> = {};
  const email = text.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/)?.[0];
  const name = text.match(/(?:\bfor|\bname(?: is|:))\s+([A-Z][\p{L}'-]+(?:\s+[A-Z][\p{L}'-]+)+)/u)?.[1]
    ?? text.match(/^([A-Z][\p{L}'-]+(?:\s+[A-Z][\p{L}'-]+)+)\s+on\b/u)?.[1];
  const date = normalize("date", text);
  const partyMatch = text.match(/(?:\btable for|\bparty of)\s+(1|2|4|6|one|two|four|six)\b|\b(1|2|4|6|one|two|four|six)\s+(?:guests?|people|persons?)\b/i);
  const party = normalize("party", partyMatch?.[1] ?? partyMatch?.[2] ?? "");
  const notes = text.match(/\bwith\s+([^.]*(?:seat|allerg|patio|outside|quiet)[^.]*)/i)?.[1]?.trim();
  if (email) profile.email = email;
  if (name) profile.name = name;
  if (date) profile.date = date;
  if (party) profile.party = party;
  if (notes) profile.notes = notes;
  return profile;
}

function normalize(field: Field, text: string): string | undefined {
  const value = text.trim();
  if (field === "name") return value || undefined;
  if (field === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : undefined;
  if (field === "party") {
    const word = value.match(/\b(1|2|4|6|one|two|four|six)\b/i)?.[1]?.toLowerCase();
    const count = ({ one: "1", two: "2", four: "4", six: "6" } as Record<string, string>)[word ?? ""] ?? word;
    return count ? `${count} ${count === "1" ? "guest" : "guests"}` : undefined;
  }
  const iso = value.match(/\b(\d{4}-\d{2}-\d{2})\b/)?.[1];
  if (iso) {
    const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) return iso;
  }
  const named = value.match(/\b(\d{1,2})\s*(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*,?\s*(\d{4})\b/i);
  if (named) {
    const month = MONTHS.indexOf(named[2]!.slice(0, 3).toLowerCase()) + 1;
    const candidate = `${named[3]}-${String(month).padStart(2, "0")}-${named[1]!.padStart(2, "0")}`;
    return normalize("date", candidate);
  }
  if (/\b(today|tomorrow)\b/i.test(value)) {
    const date = new Date();
    if (/\btomorrow\b/i.test(value)) date.setDate(date.getDate() + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
}
